import { useState, useEffect } from 'react';
import { useAuth, apiClient } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { ChallengeMemberView } from '../components/challenge/ChallengeMemberView';
import { ChallengeGestionTab } from '../components/challenge/ChallengeGestionTab';
import { InscriptionTab } from '../components/challenge/ChallengeInscriptionTab';
import { ConfigTab } from '../components/challenge/ChallengeConfigTab';
import { VisuelsTab } from '../components/challenge/ChallengeVisuelsTab';
import { TransformationsTab } from '../components/challenge/ChallengeTransformationsTab';
import { CautionsTab } from '../components/challenge/ChallengeCautionsTab';
import { ParticipantsTab } from '../components/challenge/ChallengeParticipantsTab';
import { Settings, UserPlus, Image, Sliders, Scale, Shield, Users, Camera, ChevronDown, ChevronUp } from 'lucide-react';

const ADMIN_TABS = [
  { id: 'challenge', label: 'Challenge' },
  { id: 'pesee', label: 'Pesee' },
  { id: 'participants', label: 'Participants' },
  { id: 'transfo', label: 'Transfo' },
  { id: 'admin', label: 'Administration' },
];

// ─── Administration: merged Inscription + Visuels + Config + Cautions ───────
function AdministrationTab() {
  const [section, setSection] = useState('inscription');
  const sections = [
    { id: 'inscription', label: 'Inscription', icon: UserPlus },
    { id: 'cautions', label: 'Cautions', icon: Shield },
    { id: 'visuels', label: 'Visuels', icon: Image },
    { id: 'config', label: 'Config', icon: Sliders },
  ];

  return (
    <div className="p-4 space-y-4" data-testid="administration-tab">
      <div className="flex gap-1">
        {sections.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
              section === s.id ? 'bg-[#FF6B35] text-white' : 'bg-[#1B1B1B] border border-[#2A2A2E] text-zinc-400 hover:border-zinc-500'
            }`} data-testid={`admin-section-${s.id}`}>
            <s.icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        ))}
      </div>
      {section === 'inscription' && <InscriptionTab />}
      {section === 'cautions' && <CautionsTab isAdmin={true} />}
      {section === 'visuels' && <VisuelsTab />}
      {section === 'config' && <ConfigTab />}
    </div>
  );
}

export default function ChallengePage({ embedded }) {
  const { user } = useAuth();
  const [tab, setTab] = useState('challenge');
  const [visibleMembres, setVisibleMembres] = useState(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) return;
    apiClient.get('/challenge/config')
      .then(r => setVisibleMembres(r.data?.visible_membres !== false))
      .catch(() => setVisibleMembres(true));
  }, [isAdmin]);

  // Members only see the unified Challenge view
  if (!isAdmin) {
    if (visibleMembres === null) return <div className="p-8 text-center text-zinc-500 text-sm">Chargement…</div>;
    if (visibleMembres === false) {
      return (
        <div className={embedded ? '' : 'min-h-screen bg-[#0F0F0F] pb-20'}>
          <div className="p-6">
            <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-xl p-8 text-center" data-testid="challenge-hidden">
              <Shield className="w-10 h-10 text-[#FF6B35] mx-auto mb-3" />
              <p className="text-white font-bold">Challenge momentanément masqué</p>
              <p className="text-xs text-zinc-500 mt-2">Le coach prépare des mises à jour. Reviens bientôt !</p>
            </div>
          </div>
          {!embedded && <BottomNav />}
        </div>
      );
    }
    return (
      <div className={embedded ? '' : 'min-h-screen bg-[#0F0F0F] pb-20'}>
        <ChallengeMemberView />
        {!embedded && <BottomNav />}
      </div>
    );
  }

  // Admin sees tabs
  return (
    <div className={embedded ? '' : 'min-h-screen bg-[#0F0F0F] pb-20'}>
      <div className={`sticky top-0 z-40 bg-[#0F0F0F] border-b border-[#2A2A2E]`}>
        <div className="flex overflow-x-auto no-scrollbar gap-0" style={{ WebkitOverflowScrolling: 'touch' }}>
          {ADMIN_TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`challenge-tab-${t.id}`}
              className={`min-h-[44px] px-4 text-xs font-semibold border-b-2 whitespace-nowrap flex-shrink-0 flex items-center justify-center ${
                tab === t.id ? 'text-[#FF6B35] border-[#FF6B35]' : 'text-zinc-500 border-transparent'
              }`}>{t.label}</button>
          ))}
        </div>
      </div>

      {tab === 'challenge' && <ChallengeMemberView />}
      {tab === 'pesee' && <ChallengeGestionTab />}
      {tab === 'participants' && <ParticipantsTab isAdmin={isAdmin} />}
      {tab === 'transfo' && <TransformationsTab />}
      {tab === 'admin' && <AdministrationTab />}

      {!embedded && <BottomNav />}
    </div>
  );
}
